import { Grid } from "@mui/material";
import { useChannels } from "api/channels";
import { CustomAttributesSection } from "components/CustomAttributesSection/CustomAttributesSection";
import { DateTimeInput } from "components/FormControls/DateTimeInput";
import { FormSection } from "components/FormControls/FormSection";
import { SelectInput } from "components/FormControls/Select/Select";
import { TextInput } from "components/FormControls/TextInput";
import { useMessage } from "hooks/intl";
import { useManageInfiniteScroll } from "hooks/useManageInfiniteScroll";
import { CampaignTriggersUnion } from "types/campaigns";
import { KeyValuePair } from "utilities/objects";
import { ProductType } from "./Products/Product";
import { Products } from "./Products/Products";

interface Props {
  labels?: KeyValuePair[];
  products?: ProductType[];
  errors?: any;
  setFieldError: (fiels: string, value?: string) => void;
  trigger: CampaignTriggersUnion;
}

export const TransactionDetails = ({
  labels,
  products,
  errors,
  setFieldError,
  trigger,
}: Props) => {
  const { message } = useMessage();
  const { useInfiniteChannelsList } = useChannels();

  const {
    data: infiniteChannelsList,
    size: channelsSize,
    setSize: setChannelsSize,
    isValidating,
  } = useInfiniteChannelsList({
    order: "asc",
    sortKey: "name",
  });

  const { infiniteData: channelsList, anyElementsToFetch: anyChannelsToFetch } =
    useManageInfiniteScroll(infiniteChannelsList || []);

  const isReturnTransaction = trigger === "return_transaction";

  return (
    <div>
      <FormSection title={message({ id: "transactionDetails" })}>
        <Grid container spacing={12}>
          <Grid item xs={6}>
            <TextInput
              label={message({ id: "documentNumber" })}
              name="transaction.documentNumber"
              data-test-id="campaign-simulator-transaction-document-number"
              width="100%"
            />
          </Grid>
          <Grid item xs={6}>
            <DateTimeInput
              defaultValue={null}
              format="yyyy-MM-dd HH:mmXXX"
              label={message({ id: "documentDate" })}
              name="transaction.purchasedAt"
              width="100%"
              data-test-id="campaign-simulator-transaction-document-date"
            />
          </Grid>
          <Grid item xs={6}>
            <SelectInput
              items={channelsList.map(({ channelId, name }) => ({
                id: channelId,
                label: name,
              }))}
              label={message({ id: "channelName" })}
              name="transaction.channelId"
              data-test-id="campaign-simulator-transaction-channel-id"
              loadMoreElements={() =>
                !isValidating && setChannelsSize(channelsSize + 1)
              }
              anyElementsToFetch={anyChannelsToFetch}
              isClearable
            />
          </Grid>
          <Grid item xs={6}>
            <TextInput
              label={message({ id: "purchasePlace" })}
              name="transaction.purchasePlace"
              data-test-id="campaign-simulator-transaction-purchase-place"
              width="100%"
            />
          </Grid>
          {isReturnTransaction && (
            <Grid item xs={6}>
              <TextInput
                label={message({ id: "relatedDocumentNumber" })}
                name="transaction.linkedDocumentNumber"
                data-test-id="campaign-simulator-transaction-related-document-number"
                width="100%"
              />
            </Grid>
          )}
        </Grid>
      </FormSection>
      <CustomAttributesSection
        labels={labels || []}
        name="transaction"
        sectionTitle="transactionCustomAttributes"
      />
      <FormSection
        sectionError={errors?.items}
        title={message({ id: "productsInTheTransaction" })}
      >
        <Products
          products={products}
          errors={errors?.items}
          setFieldError={setFieldError}
        />
      </FormSection>
    </div>
  );
};
